/**
 * Nombres legibles de `settings/featureFlags` (Gabor 27/10: «salen comandos, no nombres»).
 * La clave sigue siendo la técnica; aquí solo el texto que ve el admin y qué pasa al apagarlo.
 */
export interface FlagInfo {
  name: string;
  hint: string;
  group: 'Inteligencia artificial' | 'Funciones de la plataforma' | 'Seguridad y moderación';
}

/**
 * Interruptores que de verdad hacen algo hoy (27/10). El resto está en settings/featureFlags desde el
 * plan, pero el código aún no los mira: el panel los enseña como «no conectado» en vez de engañar.
 */
export const WIRED_FLAGS = new Set([
  'ai.listing_from_photos',
  'ai.listing_translation',
  'ai.search_query_translation',
  'ai.chat_translation',
  'ai.chat_scam_detector',
  'ai.photo_background',
  'ai.dispute_assistant',
  'ai.support_bot',
  'ai.social_copy',
  'forum',
  'listing_human_review',
]);

export const FLAG_INFO: Record<string, FlagInfo> = {
  'ai.listing_from_photos': {
    name: 'Crear anuncio a partir de fotos',
    hint: 'La IA rellena título, descripción, categoría y precio con las fotos.',
    group: 'Inteligencia artificial',
  },
  'ai.listing_translation': {
    name: 'Traducir anuncios',
    hint: 'Títulos a los 8 idiomas principales y botón «Traducir» en la descripción.',
    group: 'Inteligencia artificial',
  },
  'ai.search_query_translation': {
    name: 'Buscar en varios idiomas',
    hint: 'Lo que se escribe en el buscador también se busca en húngaro, español e inglés.',
    group: 'Inteligencia artificial',
  },
  'ai.chat_translation': {
    name: 'Traducir el chat',
    hint: 'Cada uno lee los mensajes en su idioma.',
    group: 'Inteligencia artificial',
  },
  'ai.chat_scam_detector': {
    name: 'Detector de estafas en el chat',
    hint: 'Oculta mensajes con pagos fuera de nuvvu, IBAN, enlaces falsos… y avisa al admin.',
    group: 'Seguridad y moderación',
  },
  'ai.price_suggestion': {
    name: 'Precio sugerido',
    hint: 'Propone un precio al publicar según anuncios parecidos.',
    group: 'Inteligencia artificial',
  },
  'ai.photo_background': {
    name: 'Quitar fondo de las fotos',
    hint: 'Estudio de fotos: fondo blanco o de color.',
    group: 'Inteligencia artificial',
  },
  'ai.semantic_search': {
    name: 'Búsqueda inteligente',
    hint: 'Encuentra por significado, no solo por palabras exactas.',
    group: 'Inteligencia artificial',
  },
  'ai.similar_items': {
    name: 'Artículos parecidos',
    hint: 'Bloque «También te puede interesar» en la ficha.',
    group: 'Inteligencia artificial',
  },
  'ai.pre_moderation': {
    name: 'Revisión automática de anuncios',
    hint: 'La IA revisa cada anuncio nuevo (prohibidos, falsificaciones, +18) antes de publicarlo.',
    group: 'Seguridad y moderación',
  },
  'ai.dispute_assistant': {
    name: 'Asistente de quejas',
    hint: 'Resume cada queja y propone una solución al admin.',
    group: 'Inteligencia artificial',
  },
  'ai.support_bot': {
    name: 'Bot de soporte',
    hint: 'Responde primero a los tickets; si se apaga, todo va directo a una persona.',
    group: 'Inteligencia artificial',
  },
  'ai.seller_suggested_replies': {
    name: 'Respuestas sugeridas al vendedor',
    hint: 'Propone respuestas rápidas en el chat.',
    group: 'Inteligencia artificial',
  },
  'ai.social_copy': {
    name: 'Textos para redes sociales',
    hint: 'Escribe los posts automáticos de Facebook/Instagram.',
    group: 'Inteligencia artificial',
  },
  'ai.duplicate_accounts': {
    name: 'Detectar cuentas duplicadas',
    hint: 'Marca usuarios que parecen la misma persona con varias cuentas.',
    group: 'Seguridad y moderación',
  },
  social_publisher: {
    name: 'Publicar en redes automáticamente',
    hint: 'Publica anuncios en Facebook/Instagram a sus horas (en el sandbox, siempre apagado).',
    group: 'Funciones de la plataforma',
  },
  handover_mode: {
    name: 'Entrega en mano',
    hint: 'Permite comprar y quedar en persona con código de entrega.',
    group: 'Funciones de la plataforma',
  },
  nuvvu_club: {
    name: 'Club nuvvu',
    hint: 'Niveles con ventajas para vendedores.',
    group: 'Funciones de la plataforma',
  },
  pro_subscriptions: {
    name: 'Suscripciones PRO',
    hint: 'Se pueden contratar planes PRO.',
    group: 'Funciones de la plataforma',
  },
  featured_packs: {
    name: 'Destacados de pago',
    hint: 'Se pueden comprar destacados.',
    group: 'Funciones de la plataforma',
  },
  bumps: {
    name: 'Subir anuncios (bumps)',
    hint: 'Se puede pagar por subir un anuncio arriba.',
    group: 'Funciones de la plataforma',
  },
  forum: {
    name: 'Foro',
    hint: 'La comunidad y sus hilos.',
    group: 'Funciones de la plataforma',
  },
  country_signup_guard: {
    name: 'Alta solo desde países permitidos',
    hint: 'Bloquea registros desde países fuera de la lista.',
    group: 'Seguridad y moderación',
  },
  listing_human_review: {
    name: 'Revisión humana de anuncios nuevos',
    hint: 'Los anuncios nuevos esperan a que un admin los apruebe en Moderación.',
    group: 'Seguridad y moderación',
  },
};

export const flagInfo = (key: string): FlagInfo =>
  FLAG_INFO[key] ?? { name: key, hint: '', group: 'Funciones de la plataforma' };
